import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Shield
} from 'lucide-react';
import type { Character } from '@/lib/api/characters';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '@/lib/utils/image';
import { cn } from '@/lib/utils';
import { BlurredMedia } from '@/components/BlurredMedia';
import { isGalleryCarouselEnabled } from '@/lib/featureFlags';
import { ProfileGalleryGrid } from './ProfileGalleryGrid';
import { ProfileGalleryCarousel } from './ProfileGalleryCarousel';
import { useTrackEvent } from '@/hooks/useTrackEvent';
import { HStack } from '@/components/core';

type GalleryFilter = 'all' | 'adult' | 'standard';

interface CharacterProfileGalleryProps {
  character: Character;
  fullHeight?: boolean;
  hideShowAllButton?: boolean;
}

export const CharacterProfileGallery: React.FC<CharacterProfileGalleryProps> = ({ character, fullHeight = true, hideShowAllButton = false }) => {
  const { user: _user } = useAuth();
  const { t } = useTranslation('characters');
  const trackEvent = useTrackEvent();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [selectedImageNsfw, setSelectedImageNsfw] = useState<boolean>(false);
  const [selectedImageShouldBlur, setSelectedImageShouldBlur] = useState<boolean>(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState<GalleryFilter>('all');

  const minSwipeDistance = 50;

  type GalleryItem = Character['gallery'][number] & { should_blur?: boolean };

  // Filter out chat & creator images and EXCLUDE profile image from gallery view
  // Then sort: FSK18 images first, then by sort_order
  const allGalleryImages: GalleryItem[] = useMemo(() => {
    const filtered = (character.gallery || [])
      .filter((img: GalleryItem) => {
        const category = (img.category || '').toLowerCase();
        return category !== 'chat' && category !== 'creator' && !img.is_profile_image;
      });

    // Sort FSK18 images first, then by sort_order
    return [...filtered].sort((a, b) => {
      // FSK18 first
      if (a.is_fsk18 && !b.is_fsk18) return -1;
      if (!a.is_fsk18 && b.is_fsk18) return 1;
      // Then by sort_order
      return (a.sort_order ?? 0) - (b.sort_order ?? 0);
    });
  }, [character.gallery]);

  // Count images by type
  const adultCount = useMemo(() => allGalleryImages.filter(img => img.is_fsk18).length, [allGalleryImages]);
  const standardCount = useMemo(() => allGalleryImages.filter(img => !img.is_fsk18).length, [allGalleryImages]);

  // Filter images based on active tab
  const galleryImages: GalleryItem[] = useMemo(() => {
    switch (activeFilter) {
      case 'adult':
        return allGalleryImages.filter(img => img.is_fsk18);
      case 'standard':
        return allGalleryImages.filter(img => !img.is_fsk18);
      default:
        return allGalleryImages;
    }
  }, [allGalleryImages, activeFilter]);

  const navigateImage = useCallback((direction: 'prev' | 'next'): void => {
    if (galleryImages.length === 0) return;
    
    let newIndex = selectedImageIndex;
    if (direction === 'prev') {
      newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : galleryImages.length - 1;
    } else {
      newIndex = selectedImageIndex < galleryImages.length - 1 ? selectedImageIndex + 1 : 0;
    }
    
    const newImage = galleryImages[newIndex];
    if (newImage) {
      setSelectedImageIndex(newIndex);
      setSelectedImage(getImageUrl(newImage.url));
      setSelectedImageNsfw(newImage.is_fsk18);
      setSelectedImageShouldBlur(Boolean(newImage.should_blur));
    }
  }, [galleryImages, selectedImageIndex]);

  // Touch handlers for swipe gestures
  const onTouchStart = (e: React.TouchEvent): void => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0] ? e.targetTouches[0].clientX : null);
  };
  
  const onTouchMove = (e: React.TouchEvent): void => {
    setTouchEnd(e.targetTouches[0] ? e.targetTouches[0].clientX : null);
  };
  
  const onTouchEnd = (): void => {
    if (touchStart == null || touchEnd == null) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && galleryImages.length > 1) {
      navigateImage('next');
    }
    if (isRightSwipe && galleryImages.length > 1) {
      navigateImage('prev');
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (!selectedImage || galleryImages.length <= 1) return;
      
      if (e.key === 'ArrowLeft') {
        navigateImage('prev');
      } else if (e.key === 'ArrowRight') {
        navigateImage('next');
      } else if (e.key === 'Escape') {
        setSelectedImage(null);
      }
    };
    
    if (selectedImage) {
      document.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage, galleryImages.length, navigateImage]);

  const handleImageClick = (imageUrl: string, isFsk18: boolean, index: number): void => {
    const image = galleryImages[index];
    const shouldBlur = image?.should_blur === true;

    setSelectedImageIndex(index);
    setSelectedImage(imageUrl);
    setSelectedImageNsfw(isFsk18);
    setSelectedImageShouldBlur(shouldBlur);

    // Track image view analytics event
    void trackEvent('image_view', 'engagement', {
      image_type: 'profile_gallery',
      image_index: index,
      total_images: galleryImages.length,
      is_fsk18: isFsk18,
      category: image?.category || 'unknown'
    }, { characterId: character.id });
  };

  if (galleryImages.length === 0) {
    return (
      <div className="design-card p-token-lg character-profile-gallery-section h-full">
        <h3 className="text-xl font-semibold design-text-primary mb-token-lg">{t('profile.gallery')}</h3>
        <div className="h-80 flex items-center justify-center design-text-secondary">
          <p>{t('profile.noImagesAvailable')}</p>
        </div>
      </div>
    );
  }

  // Check if we need to show tabs (only if there are both adult and standard images)
  const showTabs = adultCount > 0 && standardCount > 0;

  return (
    <div className={cn("design-card p-token-lg", fullHeight && "h-full")}>
      <HStack align="center" justify="between" className="mb-token-lg flex-wrap gap-token-sm">
        <h3 className="text-xl font-semibold design-text-primary">{t('profile.gallery')}</h3>

        {/* Filter Tabs - only show if there are both types */}
        {showTabs && (
          <Tabs value={activeFilter} onValueChange={(v) => setActiveFilter(v as GalleryFilter)} className="flex-shrink-0">
            <TabsList className="h-9 p-token-xs">
              <TabsTrigger value="all" className="text-xs px-token-xs sm:px-token-sm sm:text-sm h-7">
                <span className="hidden sm:inline">{t('profile.galleryFilterAll')}</span>
                <span className="sm:hidden">{t('profile.galleryFilterAll')}</span>
                <span className="ml-token-xs text-[10px] sm:text-xs opacity-70">({allGalleryImages.length})</span>
              </TabsTrigger>
              <TabsTrigger value="adult" className="text-xs px-token-xs sm:px-token-sm sm:text-sm h-7">
                <Shield className="h-3 w-3 mr-token-xs hidden sm:inline-block" />
                <span>{t('profile.galleryFilterAdult')}</span>
                <span className="ml-token-xs text-[10px] sm:text-xs opacity-70">({adultCount})</span>
              </TabsTrigger>
              <TabsTrigger value="standard" className="text-xs px-token-xs sm:px-token-sm sm:text-sm h-7">
                <span className="hidden sm:inline">{t('profile.galleryFilterStandard')}</span>
                <span className="sm:hidden">{t('profile.galleryFilterStandard')}</span>
                <span className="ml-token-xs text-[10px] sm:text-xs opacity-70">({standardCount})</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {/* Show all images button */}
        {!hideShowAllButton && allGalleryImages.length > 3 && galleryImages.length > 0 && (
          <div className="text-center">
            <Button
              variant="glass"
              onClick={() => {
                const first = galleryImages[0];
                if (first) {
                  handleImageClick(getImageUrl(first.url), first.is_fsk18, 0);
                }
              }}
              className="design-text-primary"
            >
              <ImageIcon className="h-4 w-4 mr-token-sm" />
              {t('profile.showAllImages', {count: galleryImages.length})}
            </Button>
          </div>
        )}
      </HStack>
      <div className="h-[30rem] relative character-gallery character-profile-gallery-section overflow-hidden rounded-token-card">
        {isGalleryCarouselEnabled() ? (
          <ProfileGalleryCarousel
            characterName={character.name}
            galleryImages={galleryImages}
            characterId={character.id}
            t={t}
            onImageClick={(url, isFsk18, originalIndex) => handleImageClick(url, isFsk18, originalIndex)}
          />
        ) : (
          <ProfileGalleryGrid
            characterName={character.name}
            galleryImages={galleryImages}
            characterId={character.id}
            t={t}
            onImageClick={(url, isFsk18, originalIndex) => handleImageClick(url, isFsk18, originalIndex)}
          />
        )}
      </div>

      {/* Image Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="modal-dialog-content bg-background/95 backdrop-blur-md border-white/20 p-0 overflow-hidden [&>button]:hidden" aria-describedby="image-modal-description">
          <DialogTitle className="sr-only">{t('profile.galleryImageAlt', {name: character.name})}</DialogTitle>
          <div className="sr-only">
            <p id="image-modal-description">{t('profile.imageModalDescription', {current: selectedImageIndex + 1, total: galleryImages.length})}</p>
          </div>
          <div 
            className="modal-image-container"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Navigation Arrows */}
            {galleryImages.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigateImage('prev')}
                  className="modal-navigation-button absolute left-token-md top-1/2 -translate-y-1/2 z-10 text-white bg-black/50 hover:bg-black/70 h-12 w-12"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigateImage('next')}
                  className="modal-navigation-button absolute right-token-md top-1/2 -translate-y-1/2 z-10 text-white bg-black/50 hover:bg-black/70 h-12 w-12"
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}
            
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedImage(null)}
              className="absolute top-token-md right-token-md z-10 text-white bg-black/50 hover:bg-black/70"
            >
              <X className="h-6 w-6" />
            </Button>
            
            {/* Image or Video */}
            <div className="relative w-full h-full flex items-center justify-center p-token-md">
              {selectedImage && (
                <BlurredMedia
                  src={selectedImage}
                  alt={character.name}
                  shouldBlur={selectedImageShouldBlur}
                  isFsk18={selectedImageNsfw}
                  characterId={character.id}
                  className="modal-image"
                  autoPlayVideo
                  showMuteToggle
                />
              )}
            </div>
            
            {/* Swipe indicator dots */}
            {galleryImages.length > 1 && (
              <div className="swipe-indicators">
                {galleryImages.map((_, index) => (
                  <div 
                    key={index}
                    className={cn(
                      "swipe-dot",
                      index === selectedImageIndex && "active"
                    )}
                  />
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};